import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { AchievementProvider } from "@/contexts/AchievementContext";
import { FeaturesProvider } from "@/contexts/FeaturesContext";
import { ConfirmProvider } from "@/components/ui/confirm-dialog";
import { ProtectedRoute, SubscriptionRoute, FreeTrialRoute, FeatureGatedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import SubDomainGuard from "@/components/hs/SubDomainGuard";
import ShopThemeApplier from "@/components/hs/ShopThemeApplier";
import BrandFavicon from "@/components/BrandFavicon";
import ShopTypographyStyle from "@/components/hs/ShopTypographyStyle";
import SiteStructuredData from "@/components/hs/SiteStructuredData";
import GlobalSeoGuard from "@/components/GlobalSeoGuard";
import MaintenanceGate from "@/components/MaintenanceGate";
const ReferralCookieInit = () => { useReferralCookie(); return null; };
import { OnboardingRedirect } from "@/components/OnboardingRedirect";
import { AppStartRedirect } from "@/components/AppStartRedirect";
import { TaxAdvisorRedirect } from "@/components/TaxAdvisorRedirect";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { setupTokenValidation } from "@/utils/tokenUtils";
import { CartProvider } from "@/components/hs/CartContext";
import { AffiliateProvider } from "@/components/hs/AffiliateContext";
import { useReferralCookie } from "@/components/hs/useReferralCookie";
import { CartDrawer, WishlistDrawer } from "@/components/hs/CartDrawers";
import ShopMobileBottomBar from "@/components/hs/ShopMobileBottomBar";
import "@/i18n/config";
import "@/App.css";
import "@/styles/themes/prism3d.css";
import "@/styles/rolex-green-theme.css";

// Critical pages - load immediately

// Lazy load non-critical pages for faster initial load
// Juni 2026 · Nicht sichtbare Widgets erst nach dem ersten Rendern laden (TBT/LCP).
const AccessibilityWidget = lazy(() => import("@/components/AccessibilityWidget"));
const FocusReaderOverlay = lazy(() => import("@/components/FocusReaderOverlay"));
const ReminderBanner = lazy(() => import("@/components/ReminderBanner").then(m => ({ default: m.ReminderBanner })));
const CookieBanner = lazy(() => import("@/components/CookieBanner").then(m => ({ default: m.CookieBanner })));
const InstallPrompt = lazy(() => import("@/components/InstallPrompt"));
const GlobalContactWidgets = lazy(() => import("@/components/hs/GlobalWidgets").then(m => ({ default: m.GlobalContactWidgets })));
const GlobalBackToTop = lazy(() => import("@/components/hs/GlobalWidgets").then(m => ({ default: m.GlobalBackToTop })));
const DynamicHomePage = lazy(() => import("@/pages/DynamicHomePage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const HomepageConfigPage = lazy(() => import("@/pages/HomepageConfigPage"));
const PracticesPage = lazy(() => import("@/pages/PracticesPage"));
const JournalPage = lazy(() => import("@/pages/JournalPage"));
const ProgressPage = lazy(() => import("@/pages/ProgressPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const LearningPage = lazy(() => import("@/pages/LearningPage"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const SubscriptionPage = lazy(() => import("@/pages/SubscriptionPage"));
const SubscriptionSuccessPage = lazy(() => import("@/pages/SubscriptionSuccessPage"));
const EnvironmentCheckPage = lazy(() => import("@/pages/EnvironmentCheckPage"));
const RelationshipReadinessPage = lazy(() => import("@/pages/RelationshipReadinessPage"));
const FocusTimePage = lazy(() => import("@/pages/FocusTimePage"));
const FocusGamesPage = lazy(() => import("@/pages/FocusGamesPage"));
const GoalCounterPage = lazy(() => import("@/pages/GoalCounterPage"));
const KontaktBasisPage = lazy(() => import("@/pages/KontaktBasisPage"));
const ChatbotPage = lazy(() => import("@/pages/ChatbotPage"));
const MusicRelaxationPage = lazy(() => import("@/pages/MusicRelaxationPage"));
const DailyRoutinePage = lazy(() => import("@/pages/DailyRoutinePage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const CompanyResetConfirmPage = lazy(() => import("@/pages/CompanyResetConfirmPage"));
const VCardPage = lazy(() => import("@/pages/VCardPage"));
const VCardAdminPage = lazy(() => import("@/pages/VCardAdminPage"));
const PublicVCardPage = lazy(() => import("@/pages/PublicVCardPage"));
const BlogListPage = lazy(() => import("@/pages/BlogListPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const ImpressumPage = lazy(() => import("@/pages/ImpressumPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const WiderrufsrechtPage = lazy(() => import("@/pages/WiderrufsrechtPage"));
const AffiliateDashboard = lazy(() => import("@/pages/AffiliateDashboard"));
const AffiliateApplicationPage = lazy(() => import("@/pages/AffiliateApplicationPage"));
const FavoriteQuotesPage = lazy(() => import("@/pages/FavoriteQuotesPage"));
const PlayInvitePage = lazy(() => import("@/pages/PlayInvitePage"));
const LuxuryTemptationsPage = lazy(() => import("@/pages/LuxuryTemptationsPage"));
const BudgetPage = lazy(() => import("@/pages/BudgetPage"));
const RevenueDistributionPage = lazy(() => import("@/pages/RevenueDistributionPage"));
const InventoryPage = lazy(() => import("@/pages/InventoryPage"));
const FahrtenbuchPage = lazy(() => import("@/pages/FahrtenbuchPage"));
const FeatureComparisonPage = lazy(() => import("@/pages/FeatureComparisonPage"));
const AccessibilityStatementPage = lazy(() => import("@/pages/AccessibilityStatementPage"));
const WiderrufsformularPage = lazy(() => import("@/pages/WiderrufsformularPage"));
const LeisurePlannerPage = lazy(() => import("@/pages/LeisurePlannerPage"));
const ColdCallTrainerPage = lazy(() => import("@/pages/ColdCallTrainerPage"));
const InvoicingPage = lazy(() => import("@/pages/InvoicingPage"));
const OffersPage = lazy(() => import("@/pages/OffersPage"));
const OfferSignPage = lazy(() => import("@/pages/OfferSignPage"));
const AccountingPage = lazy(() => import("@/pages/AccountingPage"));
const TravelPage = lazy(() => import("@/pages/TravelPage"));
const TaxAdvisorPortal = lazy(() => import("@/pages/TaxAdvisorPortal"));
const AdsPage = lazy(() => import("@/pages/AdsPage"));
const PersonnelPage = lazy(() => import("@/pages/PersonnelPage"));
const ProjectTimeTrackingPage = lazy(() => import("@/pages/ProjectTimeTrackingPage"));
const CompanyCalendarPage = lazy(() => import("@/pages/CompanyCalendarPage"));
const MitarbeiteraktePage = lazy(() => import("@/pages/MitarbeiteraktePage"));
const DienstplanPage = lazy(() => import("@/pages/DienstplanPage"));
const TrashPage = lazy(() => import("@/pages/TrashPage"));
const BackupPage = lazy(() => import("@/pages/BackupPage"));
const DashboardOverviewPage = lazy(() => import("@/pages/DashboardOverviewPage"));
const SalesJournalPage = lazy(() => import("@/pages/SalesJournalPage"));
const KennzahlenRechnerPage = lazy(() => import("@/pages/KennzahlenRechnerPage"));
const PartnerDetailPage = lazy(() => import("@/pages/PartnerDetailPage"));
const PartnersOverviewPage = lazy(() => import("@/pages/PartnersOverviewPage"));
const TeamPage = lazy(() => import("@/pages/TeamPage"));
const NavigationSettings = lazy(() => import("@/pages/NavigationSettings"));
const OnboardingWizardPage = lazy(() => import("@/pages/OnboardingWizardPage"));
const AcceptInvitePage = lazy(() => import("@/pages/AcceptInvitePage"));
const ShopCatalogPage = lazy(() => import("@/pages/ShopCatalogPage"));
const ShopCustomPage = lazy(() => import("@/pages/ShopCustomPage"));
const ImmobilienOverviewPage = lazy(() => import("@/pages/ImmobilienOverviewPage"));
const ShopCategoriesPage = lazy(() => import("@/pages/ShopCategoriesPage"));
const ProductDetailPage = lazy(() => import("@/pages/ProductDetailPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const ShopDesignerPage = lazy(() => import("@/pages/ShopDesignerPage"));
const PublicDesignViewPage = lazy(() => import("@/pages/PublicDesignViewPage"));
const PublicCalculatorPage = lazy(() => import("@/pages/PublicCalculatorPage"));
const SubShopPage = lazy(() => import("@/pages/SubShopPage"));
const ShopAgbPage = lazy(() => import("@/pages/ShopLegalPages").then((m) => ({ default: m.ShopAgbPage })));
const ShopDatenschutzPage = lazy(() => import("@/pages/ShopLegalPages").then((m) => ({ default: m.ShopDatenschutzPage })));
const ShopImpressumPage = lazy(() => import("@/pages/ShopLegalPages").then((m) => ({ default: m.ShopImpressumPage })));
const ShopWiderrufPage = lazy(() => import("@/pages/ShopLegalPages").then((m) => ({ default: m.ShopWiderrufPage })));
const GewaehrleistungPage = lazy(() => import("@/pages/GewaehrleistungPage"));
const InquiryPage = lazy(() => import("@/pages/InquiryPage"));
const InquirySuccessPage = lazy(() => import("@/pages/InquirySuccessPage"));
const StickfertigPage = lazy(() => import("@/pages/StickfertigPage"));
const StockNotifyVerifyPage = lazy(() => import("@/pages/StockNotifyVerifyPage"));
const ReviewInvitePage = lazy(() => import("@/pages/ReviewInvitePage"));
const KundenkontoPage = lazy(() => import("@/pages/KundenkontoPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const OrderConfirmationPage = lazy(() => import("@/pages/OrderConfirmationPage"));
const WishlistQuotePage = lazy(() => import("@/pages/WishlistQuotePage"));
const QuoteConfirmationPage = lazy(() => import("@/pages/QuoteConfirmationPage"));
const AffiliateRegisterPage = lazy(() => import("@/pages/AffiliateRegisterPage"));
const AffiliateLoginPage = lazy(() => import("@/pages/AffiliateLoginPage"));
const AffiliateDashboardPage = lazy(() => import("@/pages/AffiliateDashboardPage"));
const CoursePlayerPage = lazy(() => import("@/pages/CoursePlayerPage"));
const CourseSlideRunner = lazy(() => import("@/pages/CourseSlideRunner"));
const MyCoursesPage = lazy(() => import("@/pages/MyCoursesPage"));
const MyLicensesPage = lazy(() => import("@/pages/MyLicensesPage"));
const LicenseRedeemPage = lazy(() => import("@/pages/LicenseRedeemPage"));
const CertificateVerifyPage = lazy(() => import("@/pages/CertificateVerifyPage"));
const AdminApp = lazy(() => import("@/admin/AdminApp"));
const LmsAcademyApp = lazy(() => import("@/pages/LmsAcademyApp"));
const CoursePreviewPage = lazy(() => import("@/pages/CoursePreviewPage"));
const CourseAdminFullPreviewPage = lazy(() => import("@/pages/CourseAdminFullPreviewPage"));

// Loading fallback component - only shows the spinner if loading takes longer
// than 1 second, so fast navigations don't flash a loading circle.
const PageLoader = () => {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const timer = setTimeout(() => setShow(true), 1000);
    return () => clearTimeout(timer);
  }, []);
  if (!show) return null;
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C9A23C]"></div>
    </div>
  );
};

function App() {
  // Setup token validation on app start
  useEffect(() => {
    const intervalId = setupTokenValidation();
    return () => clearInterval(intervalId);
  }, []);

  // Hide the initial HTML splash screen as soon as React is ready.
  // The splash itself only becomes visible if loading takes longer than 1s
  // (handled in public/index.html), so fast loads show nothing at all.
  useEffect(() => {
    if (window.hideInitialSplash) {
      window.hideInitialSplash();
    }
  }, []);

  return (
    <HelmetProvider>
      {/* Robots-Meta wird global von GlobalSeoGuard verwaltet – niemand sonst darf
          hier dazwischenfunken, sonst überschreibt Helmet den Testmodus. */}
      <SiteStructuredData />
      <GlobalSeoGuard />
      <ThemeProvider>
        <AccessibilityProvider>
        <FeaturesProvider>
        <SubscriptionProvider>
          <AchievementProvider>
          <ConfirmProvider>
          <CartProvider>
          <AffiliateProvider>
          <div className="App min-h-screen bg-background text-foreground transition-colors duration-300">
            <BrowserRouter>
              <a href="#main-content" className="skip-to-content" data-testid="skip-to-content">
                Zum Inhalt springen
              </a>
              <ScrollToTop />
              <SubDomainGuard />
              <ReferralCookieInit />
              <ShopThemeApplier />
              <BrandFavicon />
              <ShopTypographyStyle />
              <Suspense fallback={null}>
                <GlobalContactWidgets />
                <GlobalBackToTop />
              </Suspense>
              <OnboardingRedirect />
              <AppStartRedirect />
              <TaxAdvisorRedirect />
              <CartDrawer />
              <WishlistDrawer />
              <ShopMobileBottomBar />
              <Suspense fallback={null}>
                <ReminderBanner />
                <CookieBanner />
                <InstallPrompt />
                <AccessibilityWidget />
                <FocusReaderOverlay />
              </Suspense>
              <Suspense fallback={<PageLoader />}>
          <ErrorBoundary>
          <MaintenanceGate>
          <main id="main-content" tabIndex={-1}>
          <Routes>
            {/* ============================================
                PUBLIC ROUTES - No login required
            ============================================ */}
            <Route path="/" element={<ShopCatalogPage />} />
            {/* Iter306ah: Alternativ-URL für den Shop-Katalog. Greift, wenn die
                Startseite auf eine Custom-Page/Blog umgebogen wurde und der
                Katalog dennoch über einen Menüeintrag erreichbar sein soll.
                Die Komponente erkennt selbst am `location.pathname`, dass sie
                nicht auf `/` läuft und überspringt den Override-Redirect. */}
            <Route path="/shop-katalog" element={<ShopCatalogPage />} />
            <Route path="/shop" element={<ShopCatalogPage />} />
            <Route path="/kategorien" element={<ShopCategoriesPage />} />
            <Route path="/kategorie" element={<ShopCategoriesPage />} />
            <Route path="/categories" element={<ShopCategoriesPage />} />
            <Route path="/partner-werden" element={<AffiliateRegisterPage />} />
            <Route path="/partner-login" element={<AffiliateLoginPage />} />
            <Route path="/partner" element={<AffiliateDashboardPage />} />
            <Route path="/seite/:slug" element={<ShopCustomPage />} />
            <Route path="/kurs/:courseId/player" element={<CoursePlayerPage />} />
            <Route path="/kurs/:courseId/lernen" element={<CourseSlideRunner />} />
            <Route path="/kundenkonto/kurse" element={<MyCoursesPage />} />
            <Route path="/kundenkonto/lizenzen" element={<MyLicensesPage />} />
            <Route path="/lizenz/einloesen" element={<LicenseRedeemPage />} />
            <Route path="/zertifikat/:token" element={<CertificateVerifyPage />} />
            <Route path="/p/:slug" element={<ShopCustomPage />} />
            <Route path="/immobilien" element={<ImmobilienOverviewPage />} />
            <Route path="/companylife" element={<DynamicHomePage />} />
            <Route path="/home-classic" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/registrieren" element={<RegisterPage />} />
            <Route path="/einladung/annehmen" element={<AcceptInvitePage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/passwort-zuruecksetzen" element={<ResetPasswordPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/features" element={<FeatureComparisonPage />} />
            <Route path="/funktionen" element={<FeatureComparisonPage />} />
            <Route path="/rechner" element={<KennzahlenRechnerPage />} />
            <Route path="/kennzahlen-rechner" element={<KennzahlenRechnerPage />} />
            
            {/* Legal Pages — Feb 2026: Shop-Layout + Content aus /api/legal.
                Alte Companylife-Pages bleiben nur unter /privacy, /terms erreichbar. */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/datenschutz" element={<ShopDatenschutzPage />} />
            <Route path="/impressum" element={<ShopImpressumPage />} />
            <Route path="/legal-notice" element={<ShopImpressumPage />} />
            <Route path="/barrierefreiheit" element={<AccessibilityStatementPage />} />
            <Route path="/accessibility" element={<AccessibilityStatementPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/agb" element={<ShopAgbPage />} />
            <Route path="/widerruf" element={<ShopWiderrufPage />} />
            <Route path="/gewaehrleistung" element={<GewaehrleistungPage />} />
            <Route path="/gewährleistung" element={<GewaehrleistungPage />} />
            <Route path="/warranty" element={<GewaehrleistungPage />} />
            <Route path="/shop/gewaehrleistung" element={<GewaehrleistungPage />} />
            <Route path="/withdrawal" element={<ShopWiderrufPage />} />
            <Route path="/widerrufsformular" element={<WiderrufsformularPage />} />
            <Route path="/withdrawal-form" element={<WiderrufsformularPage />} />
            
            {/* Game Invitation - Public (view-only for non-subscribers) */}
            <Route path="/play/:gameId" element={<PlayInvitePage />} />

            {/* Public offer signing (no login) */}
            <Route path="/angebot-signieren/:token" element={<OfferSignPage />} />
            <Route path="/sign-offer/:token" element={<OfferSignPage />} />

            {/* ============================================
                FREE WITH LOGIN - Limited free access
            ============================================ */}
            {/* Practices - Free preview (login required) */}
            <Route path="/practices" element={
              <ProtectedRoute>
                <PracticesPage />
              </ProtectedRoute>
            } />
            
            {/* Focus Timer - 2 free uses, then subscription required */}
            <Route path="/focus" element={
              <FreeTrialRoute maxFreeUses={2} featureKey="focus_timer">
                <FocusTimePage />
              </FreeTrialRoute>
            } />

            {/* Goal Counter - Available for subscribers */}
            <Route path="/goal-counter" element={
              <FeatureGatedRoute featureId="goals">
                <GoalCounterPage />
              </FeatureGatedRoute>
            } />

            {/* Kontakt Basis - CRM (Premium Addon) */}
            <Route path="/kontakt-basis" element={
              <FeatureGatedRoute featureId="crm">
                <KontaktBasisPage />
              </FeatureGatedRoute>
            } />

            {/* Budget Tool (Premium) */}
            <Route path="/budget" element={
              <FeatureGatedRoute featureId="budget">
                <BudgetPage />
              </FeatureGatedRoute>
            } />

            <Route path="/umsatzverteilung" element={
              <FeatureGatedRoute featureId="revenue_distribution">
                <RevenueDistributionPage />
              </FeatureGatedRoute>
            } />

            <Route path="/lager" element={
              <FeatureGatedRoute featureId="inventory">
                <InventoryPage />
              </FeatureGatedRoute>
            } />

            <Route path="/fahrtenbuch" element={
              <FeatureGatedRoute featureId="fahrtenbuch">
                <FahrtenbuchPage />
              </FeatureGatedRoute>
            } />

            {/* ============================================
                SUBSCRIPTION REQUIRED - Full features
            ============================================ */}
            <Route path="/journal" element={
              <FeatureGatedRoute featureId="diary">
                <JournalPage />
              </FeatureGatedRoute>
            } />
            <Route path="/tagebuch" element={
              <FeatureGatedRoute featureId="diary">
                <JournalPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/progress" element={
              <FeatureGatedRoute featureId="progress">
                <ProgressPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/calendar" element={
              <FeatureGatedRoute featureId="calendar">
                <CalendarPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/learning" element={
              <FeatureGatedRoute featureId="elearning">
                <LearningPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/projects" element={
              <FeatureGatedRoute featureId="projects">
                <ProjectsPage />
              </FeatureGatedRoute>
            } />
            <Route path="/leisure-planner" element={
              <FeatureGatedRoute featureId="leisure_planner">
                <LeisurePlannerPage />
              </FeatureGatedRoute>
            } />
            <Route path="/cold-call-trainer" element={
              <FeatureGatedRoute featureId="cold_call_trainer">
                <ColdCallTrainerPage />
              </FeatureGatedRoute>
            } />
            <Route path="/kaltakquise-trainer" element={
              <FeatureGatedRoute featureId="cold_call_trainer">
                <ColdCallTrainerPage />
              </FeatureGatedRoute>
            } />
            <Route path="/rechnungen" element={
              <FeatureGatedRoute featureId="invoicing">
                <InvoicingPage />
              </FeatureGatedRoute>
            } />
            <Route path="/invoicing" element={
              <FeatureGatedRoute featureId="invoicing">
                <InvoicingPage />
              </FeatureGatedRoute>
            } />
            <Route path="/angebote" element={
              <FeatureGatedRoute featureId="offers">
                <OffersPage />
              </FeatureGatedRoute>
            } />
            <Route path="/offers" element={
              <FeatureGatedRoute featureId="offers">
                <OffersPage />
              </FeatureGatedRoute>
            } />
            <Route path="/buchhaltung" element={
              <FeatureGatedRoute featureId="accounting">
                <AccountingPage />
              </FeatureGatedRoute>
            } />
            <Route path="/accounting" element={
              <FeatureGatedRoute featureId="accounting">
                <AccountingPage />
              </FeatureGatedRoute>
            } />
            <Route path="/reisekosten" element={
              <FeatureGatedRoute featureId="travel">
                <TravelPage />
              </FeatureGatedRoute>
            } />
            <Route path="/steuerberater" element={
              <ProtectedRoute>
                <TaxAdvisorPortal />
              </ProtectedRoute>
            } />
            <Route path="/werbung" element={
              <ProtectedRoute>
                <AdsPage />
              </ProtectedRoute>
            } />
            <Route path="/ads" element={
              <ProtectedRoute>
                <AdsPage />
              </ProtectedRoute>
            } />
            <Route path="/personalzeit" element={
              <FeatureGatedRoute featureId="personnel">
                <PersonnelPage />
              </FeatureGatedRoute>
            } />
            <Route path="/projektzeiten" element={
              <FeatureGatedRoute featureId="time_tracking">
                <ProjectTimeTrackingPage />
              </FeatureGatedRoute>
            } />
            <Route path="/firmenkalender" element={
              <FeatureGatedRoute featureId="company_calendar">
                <CompanyCalendarPage />
              </FeatureGatedRoute>
            } />
            <Route path="/mitarbeiterakte" element={
              <FeatureGatedRoute featureId="employee_records">
                <MitarbeiteraktePage />
              </FeatureGatedRoute>
            } />
            <Route path="/dienstplan" element={
              <FeatureGatedRoute featureId="shift_planner">
                <DienstplanPage />
              </FeatureGatedRoute>
            } />
            <Route path="/papierkorb" element={
              <ProtectedRoute>
                <TrashPage />
              </ProtectedRoute>
            } />
            <Route path="/datensicherung" element={
              <ProtectedRoute>
                <BackupPage />
              </ProtectedRoute>
            } />
            <Route path="/uebersicht" element={
              localStorage.getItem('token')
                ? (
                  <SubscriptionRoute>
                    <DashboardOverviewPage />
                  </SubscriptionRoute>
                )
                : (<DashboardOverviewPage demo />)
            } />
            <Route path="/team" element={
              <ProtectedRoute>
                <TeamPage />
              </ProtectedRoute>
            } />
            <Route path="/mitarbeiter" element={
              <ProtectedRoute>
                <TeamPage />
              </ProtectedRoute>
            } />
            <Route path="/navigation-einstellungen" element={
              <ProtectedRoute>
                <NavigationSettings />
              </ProtectedRoute>
            } />
            <Route path="/einrichtung" element={
              <ProtectedRoute>
                <OnboardingWizardPage />
              </ProtectedRoute>
            } />
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <OnboardingWizardPage />
              </ProtectedRoute>
            } />
            <Route path="/overview" element={
              <SubscriptionRoute>
                <DashboardOverviewPage />
              </SubscriptionRoute>
            } />
            <Route path="/vertriebs-journal" element={
              <SubscriptionRoute>
                <SalesJournalPage />
              </SubscriptionRoute>
            } />
            <Route path="/sales-journal" element={
              <SubscriptionRoute>
                <SalesJournalPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/relationship-readiness" element={
              <FeatureGatedRoute featureId="resilience">
                <RelationshipReadinessPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/focus/games" element={
              <FeatureGatedRoute featureId="focus_games">
                <FocusGamesPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/focus-games" element={
              <FeatureGatedRoute featureId="focus_games">
                <FocusGamesPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/chatbot" element={
              <FeatureGatedRoute featureId="coach">
                <ChatbotPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/music" element={
              <FeatureGatedRoute featureId="music">
                <MusicRelaxationPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/daily-routine" element={
              <FeatureGatedRoute featureId="daily_routine">
                <DailyRoutinePage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />

            <Route path="/profil/firma-zuruecksetzen" element={
              <ProtectedRoute>
                <CompanyResetConfirmPage />
              </ProtectedRoute>
            } />

            <Route path="/visitenkarte" element={
              <FeatureGatedRoute featureId="vcard">
                <VCardPage />
              </FeatureGatedRoute>
            } />

            <Route path="/visitenkarte/team" element={
              <FeatureGatedRoute featureId="vcard">
                <VCardAdminPage />
              </FeatureGatedRoute>
            } />

            <Route path="/karte/:companySlug/:employeeSlug" element={<PublicVCardPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* ============================================
                SHOP – Hauptstadtstickerei (Public)
            ============================================ */}
            <Route path="/shop" element={<ShopCatalogPage />} />
            <Route path="/shop/stickfertig" element={<StickfertigPage />} />
            <Route path="/shop/notify/verify/:token" element={<StockNotifyVerifyPage />} />
            <Route path="/review/:orderId/:itemIndex" element={<ReviewInvitePage />} />
            <Route path="/shop/:slug" element={<ProductDetailPage />} />
            <Route path="/shop/:slug/designer" element={<ShopDesignerPage />} />
            <Route path="/design/:designId" element={<PublicDesignViewPage />} />
            <Route path="/rechner/:slug" element={<PublicCalculatorPage />} />
            <Route path="/themenwelten/:slug" element={<SubShopPage />} />

            {/* Shop-spezifische Rechtstexte (Platzhalter-Vorlagen) */}
            <Route path="/shop/agb" element={<ShopAgbPage />} />
            <Route path="/shop/datenschutz" element={<ShopDatenschutzPage />} />
            <Route path="/shop/impressum" element={<ShopImpressumPage />} />
            <Route path="/shop/widerruf" element={<ShopWiderrufPage />} />

            {/* Individuelle Anfragemaske */}
            <Route path="/anfrage" element={<InquiryPage />} />
            <Route path="/anfrage/danke" element={<InquirySuccessPage />} />

            {/* Checkout & Wishlist-Quote */}
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/danke/:orderId" element={<OrderConfirmationPage />} />
            <Route path="/merkzettel/angebot" element={<WishlistQuotePage />} />
            <Route path="/merkzettel/danke/:quoteId" element={<QuoteConfirmationPage />} />

            {/* ============================================
                KUNDENKONTO – Shop-Kundenlogin & -Bereich
            ============================================ */}
            <Route path="/kundenkonto/*" element={<KundenkontoPage />} />
            
            <Route path="/favorite-quotes" element={
              <ProtectedRoute>
                <FavoriteQuotesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/luxury" element={
              <FeatureGatedRoute featureId="luxury">
                <LuxuryTemptationsPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/homepage-config" element={
              <ProtectedRoute>
                <HomepageConfigPage />
              </ProtectedRoute>
            } />
            
            <Route path="/environment" element={
              <FeatureGatedRoute featureId="environment">
                <EnvironmentCheckPage />
              </FeatureGatedRoute>
            } />
            
            {/* ============================================
                AFFILIATE ROUTES - Login required
            ============================================ */}
            <Route path="/partner" element={
              <ProtectedRoute>
                <AffiliateDashboard />
              </ProtectedRoute>
            } />
            <Route path="/partner/bewerben" element={<AffiliateApplicationPage />} />
            <Route path="/unsere-partner" element={<PartnersOverviewPage />} />
            <Route path="/partner/:slug" element={<PartnerDetailPage />} />
            <Route path="/affiliate" element={
              <ProtectedRoute>
                <AffiliateDashboard />
              </ProtectedRoute>
            } />
            <Route path="/affiliate/apply" element={<AffiliateApplicationPage />} />
            
            {/* ============================================
                ADMIN ROUTES - Admin role required
            ============================================ */}
            <Route path="/admin221542/*" element={<AdminApp />} />
            <Route path="/lms/*" element={<LmsAcademyApp />} />
            <Route path="/kurs/:courseId/vorschau" element={<CoursePreviewPage />} />
            <Route path="/admin221542/elearning/:courseId/preview-edited" element={<CourseAdminFullPreviewPage />} />
            {/* Catch-all: unknown routes redirect home instead of rendering a blank page */}
            {/* Juni 2026 · Fallback: Extra-Seiten auch ohne /seite-Prefix erreichbar */}
            <Route path="/:slug" element={<ShopCustomPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </main>
          </MaintenanceGate>
          </ErrorBoundary>
          </Suspense>
          </BrowserRouter>
          <Toaster position="top-center" />
        </div>
        </AffiliateProvider>
        </CartProvider>
        </ConfirmProvider>
        </AchievementProvider>
      </SubscriptionProvider>
        </FeaturesProvider>
        </AccessibilityProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;